<template>
  <!-- センターモーダル時はTeleportでbody直下に配置（親要素のtransform等の影響を回避） -->
  <Teleport to="body" :disabled="!isCenterModal">
    <!-- 4画面センターモーダル用オーバーレイ背景 -->
    <div
        v-if="isCenterModal && visible"
        class="fw-center-modal-overlay"
        :style="{ zIndex: zIndex - 1 }"
        @click="close($event)"
    ></div>
    <div
        :id="windowId"
        v-show="visible && (!isPrint || isAllowedInPrintMode) && !isFadingIn"
        class="draggable-div"
        :class="[{ maximized: isMaximized, modal: isModal, 'center-modal': isCenterModal, 'bottom-sheet': isBottomSheet, 'fixed-position': useFixedPosition }, type]"
        :style="windowStyle"
        v-bind="$attrs"
        @pointerdown="onDivPointerDown"
    >
    <!-- ヘッダー（normal のみ表示） -->
    <div v-if="headerShown" class="header" ref="header" :style="{ cursor: (isModal || isCenterModal) ? 'default' : 'move', touchAction: (isModal || isCenterModal) ? 'auto' : 'none', backgroundColor: headerColor || undefined }">
      <!-- タイトル：プレーンテキスト/HTML 切替（小画面時はshowTitleOnMobileで制御） -->
      <span v-if="shouldShowTitle && !titleIsHtml" class="title" :title="title">{{ title }}</span>
      <span v-else-if="shouldShowTitle && titleIsHtml" class="title" :title="title" v-html="title"></span>

      <!-- カスタムアイコン（子から送られたアイコン） -->
      <div v-if="customIcons.length > 0" class="custom-icons" :style="{ right: customIconsRightPosition }">
        <span
            v-for="(iconDef, idx) in customIcons"
            :key="idx"
            class="custom-icon-wrapper"
        >
          <MiniTooltip
              v-if="iconDef.tooltip"
              :text="iconDef.tooltip"
              :offset-x="0"
              :offset-y="6"
          >
            <v-btn
                icon
                variant="text"
                size="small"
                density="comfortable"
                :aria-label="iconDef.eventName"
                :disabled="iconDef.disabled"
                v-track="iconDef.track"
                @click.stop="handleCustomIconClick(iconDef)"
            >
              <!-- SVGアイコン（iconDef.svgがあれば使用） -->
              <span v-if="iconDef.svg" class="custom-svg-icon" :class="iconDef.iconClass" v-html="iconDef.svg"></span>
              <!-- MDIアイコン（従来通り） -->
              <v-icon v-else :icon="iconDef.icon" :class="iconDef.iconClass" />
            </v-btn>
          </MiniTooltip>
          <v-btn
              v-else
              icon
              variant="text"
              size="small"
              density="comfortable"
              :aria-label="iconDef.eventName"
              :disabled="iconDef.disabled"
              v-track="iconDef.track"
              @click.stop="handleCustomIconClick(iconDef)"
          >
            <!-- SVGアイコン（iconDef.svgがあれば使用） -->
            <span v-if="iconDef.svg" class="custom-svg-icon" :class="iconDef.iconClass" v-html="iconDef.svg"></span>
            <!-- MDIアイコン（従来通り） -->
            <v-icon v-else :icon="iconDef.icon" :class="iconDef.iconClass" />
          </v-btn>
        </span>
      </div>

      <!-- コンテキストヘルプ「?」アイコン（マッピングがある場合のみ表示） -->
      <MiniTooltip v-if="contextHelpInfo" :text="`${contextHelpInfo.title}のヘルプ`" :offset-x="0" :offset-y="6">
        <button
            class="context-help-btn"
            :style="{ right: contextHelpRight }"
            aria-label="コンテキストヘルプ"
            @click.stop="openContextHelp"
        >
?
        </button>
      </MiniTooltip>

      <!-- Win風: 拡大/元に戻す（任意） / モーダル時は非表示 -->
      <div v-if="showMaxRestore && !isModal" class="window-controls">
        <v-btn
            v-if="!isMaximized"
            icon
            variant="text"
            size="small"
            density="comfortable"
            :aria-label="'拡大'"
            @click.stop="maximize"
        >
          <v-icon icon="mdi-window-maximize" />
        </v-btn>
        <v-btn
            v-else
            icon
            variant="text"
            size="small"
            density="comfortable"
            :aria-label="'元に戻す'"
            @click.stop="restore"
        >
          <v-icon icon="mdi-window-restore" />
        </v-btn>
      </div>

      <!-- Close -->
      <button class="close-btn" @click.stop="close($event)">×</button>
    </div>

    <!-- simple の場合、ヘッダー外にボタン -->
    <button v-else class="close-btn" @click.stop="close($event)">×</button>

    <!-- コンテンツ領域（overflow は props で制御。既定は var(--fw-overflow, hidden)） -->
    <div
        class="content"
        :style="contentBoxStyle"
    >
      <slot v-if="hasBeenVisible" />
    </div>

    <!-- リサイズハンドル（ESLint対策: v-if を wrapper に）。モーダルは不可 -->
    <template v-if="resizableEffective">
      <div
          v-for="dir in resizerDirs"
          :key="dir"
          :class="['resizer', dir]"
          @pointerdown.stop="startResize($event, dir)"
      ></div>
    </template>
  </div>
  </Teleport>
</template>

<script>
import MiniTooltip from "@/components/MiniTooltip.vue";
import { getNextZIndex } from "@/js/downLoad";
import { mapState } from "vuex";
import { getContextHelp } from "@/js/contextHelpMapping";

export default {
  name: "FloatingWindow",
  inheritAttrs: false,
  components: { MiniTooltip },
  emits: ["close", "maximize", "restore", "width-changed", "height-changed"],
  props: {
    windowId: { type: String, required: true },
    title: { type: String, default: "" },
    /** タイトルを HTML として描画するか（デフォルトはテキスト） */
    titleIsHtml: { type: Boolean, default: false },

    /** ヘッダーの背景色（未指定時は --main-color） */
    headerColor: { type: String, default: '' },

    /** ヘッダーに表示するカスタムアイコン配列 */
    customIcons: {
      type: Array,
      default: () => [],
      // 各要素: { icon: 'mdi-xxx', eventName: 'custom-event', tooltip?: '説明文' }
    },

    // 初期サイズ・位置
    defaultWidth: { type: [Number, String], default: 200 },  // Number or 'auto'
    defaultHeight: { type: [Number, String], default: 'auto' },
    defaultTop: { type: Number, default: 65 },
    defaultLeft: { type: Number, default: 10 },
    defaultRight: { type: Number, default: 0 },

    keepAspectRatio: { type: Boolean, default: false },
    showMaxRestore: { type: Boolean, default: false },

    /** このウィンドウ自体をユーザーがリサイズできるか（通常時） */
    resizable: { type: Boolean, default: true },

    /** モーダル表示にするか（true: 画面中央・固定サイズ・ドラッグ不可・リサイズ不可） */
    isModal: { type: Boolean, default: false },

    /** ボトムシートモードを使用するか（isSmall500 && isModal 時のみ有効、明示的にtrueを指定したFWのみ適用） */
    bottomSheet: { type: Boolean, default: false },

    /** 小画面時にタイトルを表示するか（カスタムアイコンは常に表示） */
    showTitleOnMobile: { type: Boolean, default: true },

    type: {
      type: String,
      default: "normal",
      validator: (v) => ["simple", "normal"].includes(v),
    },

    /** 左右アンカー（右寄せウィンドウを素直に扱う） */
    anchor: {
      type: String,
      default: "left",
      validator: (v) => ["left", "right"].includes(v),
    },

    /** 端スナップ距離(px) */
    snapPx: { type: Number, default: 12 },

    /** スナップ選択（'off'|'x'|'y'|'xy'|'edges'） */
    // ★ 既定を 'y' に変更（水平は吸着しない）
    snap: {
      type: String,
      default: "y",
      validator: (v) => ["off", "x", "y", "xy", "edges"].includes(v),
    },

    /** snap==='edges' のときに適用するエッジ集合 */
    snapEdges: {
      type: Array,
      default: () => ["top", "bottom", "left", "right"],
    },

    /** コンテキストヘルプを外部から上書き（{ title, page } or null） */
    helpOverride: { type: Object, default: null },

    /** 位置/サイズの保存（全体ON/OFF） */
    persist: { type: Boolean, default: true },

    /** 位置の保存（persist が true の場合に有効） */
    persistPosition: { type: Boolean, default: true },

    /** サイズの保存（persist が true かつ resizable が true の場合に有効） */
    persistSize: { type: Boolean, default: true },

    /** 保存キー（未指定時は windowId を使用） */
    storageKey: { type: String, default: "" },

    /** 選択式永続化: 'never' | 'normal' | 'always' */
    persistMode: {
      type: String,
      default: "normal",
      validator: (v) => ["never", "normal", "always"].includes(v),
    },

    /** content 要素の overflow を直接指定 */
    overflow: { type: String, default: "" },

    /** defaultHeight が auto のときの高さ上限（viewport 下端からの余白 px） */
    autoHeightCapOffset: { type: Number, default: 80 },

    /** 初回表示時に遅延フェードインするか（位置計算後に表示するため） */
    delayedFadeIn: { type: Boolean, default: false },

    /** フェードイン遅延時間(ms) */
    fadeInDelay: { type: Number, default: 100 },

    /** 画面全体を基準に配置する（position: fixed） */
    useFixedPosition: { type: Boolean, default: false },

    /** trueの場合、バツボタンクリック時に自動で閉じず、closeイベントのみ発火する（確認ダイアログ用） */
    preventAutoClose: { type: Boolean, default: false },
  },

  data() {
    return {
      top: this.defaultTop,
      left: this.defaultLeft,
      right: this.defaultRight,
      width: typeof this.defaultWidth === 'number' ? this.defaultWidth : 0,
      autoWidth: this.defaultWidth === 'auto',
      height: typeof this.defaultHeight === 'number' ? this.defaultHeight : 0,
      autoHeight: this.defaultHeight === 'auto' || this.defaultHeight === '' || this.defaultHeight === null || this.defaultHeight === undefined,
      zIndex: 0,

      dragging: false,
      resizing: false,
      resizeDir: "",
      startX: 0,
      startY: 0,
      startLeft: 0,
      startTop: 0,
      startWidth: 0,
      startHeight: 0,
      originalAspect: 1,

      // 最大化
      isMaximized: false,
      preMaxState: null,

      // auto高さクランプ管理
      isAutoClamped: false,
      contentMo: null,

      // 遅延フェードイン用（delayedFadeIn有効時は初回は非表示にする）
      isFadingIn: this.delayedFadeIn,

      // スロット遅延レンダリング: 一度でも表示されたらtrue（以後はマウント維持）
      hasBeenVisible: false,

      // 位置がストレージから復元されたかどうか
      positionRestoredFromStorage: false,
    };
  },

  computed: {
    ...mapState(["mapillaryZindex", "isPrint", "isSmall500", "is4ScreenMode"]),

    // 小画面判定
    isSmallScreen() {
      return this.isSmall500;
    },
    // 4画面モード時のセンターモーダル判定（既存のisModalは除く）
    isCenterModal() {
      return this.is4ScreenMode && !this.isModal;
    },
    // ボトムシートモード（スマホ + モーダル指定 + bottomSheet propがtrue時のみ）
    isBottomSheet() {
      return this.isSmall500 && this.isModal && this.bottomSheet;
    },
    // モーダルのときは強制的にリサイズ不可（センターモーダルはリサイズ可能）
    resizableEffective() {
      return this.isModal ? false : this.resizable;
    },
    // ウィンドウスタイル
    windowStyle() {
      const widthStyle = this.autoWidth ? 'auto' : (this.width + 'px')
      // ボトムシートモード（スマホ + モーダル）: 下半分に固定
      if (this.isBottomSheet) {
        return {
          position: 'fixed',
          top: '50%',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '50%',
          zIndex: this.zIndex,
          borderRadius: '16px 16px 0 0',
        };
      }
      // センターモーダル時は中央固定（インラインスタイルで強制：CSSクラスの遅延適用による位置飛び防止）
      if (this.isCenterModal) {
        return {
          position: 'fixed',
          top: '50%',
          left: '50%',
          right: 'auto',
          transform: 'translate(-50%, -50%)',
          width: widthStyle,
          height: this.rootHeightStyle,
          zIndex: this.zIndex,
        };
      }
      // 通常時
      return {
        // useFixedPositionの場合はposition: absoluteでコンテナ基準に配置
        // （以前はfixedだったがバナー追加後にリサイズ時の位置ずれが発生したためabsoluteに変更）
        ...(this.useFixedPosition ? { position: 'absolute' } : {}),
        top: this.top + 'px',
        ...(this.anchor === 'right' ? { right: this.right + 'px' } : { left: this.left + 'px' }),
        width: widthStyle,
        height: this.rootHeightStyle,
        cursor: this.isModal ? 'default' : (this.fullDraggable ? 'move' : 'default'),
        zIndex: this.zIndex,
      };
    },

    // 印刷モード時でも表示を許可するウィンドウかどうか
    isAllowedInPrintMode() {
      return this.windowId.startsWith('draw-config-window-') ||
             this.windowId.startsWith('draw-share-window-') ||
             this.windowId.startsWith('layer-window-') ||
             this.windowId === 'qrcode';
    },

    // Vuex: このIDの表示状態
    visible: {
      get() {
        return !!this.$store.state.floatingWindows[this.windowId];
      },
      set(val) {
        this.$store.commit("setFloatingVisible", { id: this.windowId, visible: val });
      },
    },
    headerShown() {
      return this.type === "normal";
    },
    shouldShowTitle() {
      // 小画面でshowTitleOnMobileがfalseの場合はタイトルを非表示
      if (this.isSmallScreen && !this.showTitleOnMobile) {
        return false;
      }
      return true;
    },
    headerHeight() {
      if (!this.headerShown) return 0;
      // モーダル時はヘッダーが大きい（48px）
      return this.isModal ? 48 : 32;
    },
    fullDraggable() {
      return this.type === "simple";
    },
    resizerDirs() {
      return [
        "top-left", "top", "top-right",
        "left",               "right",
        "bottom-left", "bottom", "bottom-right"
      ];
    },
    rootHeightStyle() {
      if (this.isMaximized) return this.height + 'px';
      return this.autoHeight ? 'auto' : (this.height + 'px');
    },
    contentBoxStyle() {
      if (this.autoHeight) {
        return { height: 'auto', overflow: this.contentOverflowStyle };
      }
      return [
        this.headerShown ? { height: `calc(100% - ${this.headerHeight}px)` } : { height: '100%' },
        { overflow: this.contentOverflowStyle }
      ];
    },
    contentOverflowStyle() {
      const v = (this.overflow || '').trim();
      // モーダル or クランプ時は自動スクロールにする
      if (this.isModal || this.isAutoClamped) return 'auto';
      // 通常時は props/変数優先
      return v || 'var(--fw-overflow, hidden)';
    },
    customIconsRightPosition() {
      // バツボタンの幅(約40px) + margin(8px) = 48px
      // window-controls が表示される場合は、さらにそのボタン幅(28px) + gap(6px) = 82px
      const hasWindowControls = this.showMaxRestore && !this.isModal;
      const hasCtxHelp = this.contextHelpInfo ? 20 : 0;
      return hasWindowControls ? `${82 + hasCtxHelp}px` : `${48 + hasCtxHelp}px`;
    },
    /** コンテキストヘルプ情報（helpOverride優先、なければマッピングから取得） */
    contextHelpInfo () {
      if (this.helpOverride) return this.helpOverride
      return getContextHelp(this.windowId)
    },
    /** コンテキストヘルプボタンのright位置 */
    contextHelpRight () {
      // close-btn = right:8px (~28px幅) → 36px
      // window-controls がある場合 = right:48px + btn幅(~28px) + gap → 82px
      const hasWindowControls = this.showMaxRestore && !this.isModal
      if (this.isModal) return '50px'
      return hasWindowControls ? '82px' : '36px'
    },
  },

  methods: {
    /** コンテキストヘルプを開く */
    openContextHelp () {
      const info = this.contextHelpInfo
      if (!info) return
      this.$store.commit('setContextHelpContext', { title: info.title, page: info.page })
      this.$store.commit('setFloatingVisible', { id: 'ai-help-chat', visible: true })
    },

    /* -------------------- ウィンドウ要素取得 -------------------- */
    getWindowElement() {
      return document.getElementById(this.windowId);
    },

    /* -------------------- モーダルレイアウト適用 -------------------- */
    applyModalLayout() {
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      this.isMaximized = false;
      this.autoHeight = false;
      this.width = vw;
      this.height = vh;

      // 画面いっぱいに配置（隙間なし）
      this.top = 0;
      if (this.anchor === 'right') {
        this.right = 0;
      } else {
        this.left = 0;
      }

      this.$nextTick(() => {
        this.$emit('width-changed', this.width);
        this.$emit('height-changed', this.height);
      });
    },

    applyNormalLayout() {
      this.restoreFromStorage();
      this.ensureInitialIfOffscreen();
      this.$nextTick(() => this.updateAutoHeightClamp());
    },

    currentHeight() {
      if (this.autoHeight) {
        const el = this.getWindowElement();
        if (el && el.offsetHeight) return el.offsetHeight;
      }
      return this.height;
    },

    /* -------------------- 永続化 -------------------- */
    saveToStorage() {
      // persist または persistPosition のいずれかがtrueなら保存処理を行う
      if (!this.persist && !this.persistPosition) return;
      if (this.persistMode === "never") return;
      if (this.persistMode === "normal" && this.isMaximized) return; // 最大化中は保存しない

      const key = this.storageKey || `fw:${this.windowId}`;
      const payload = {
        a: this.anchor,
        max: false,
      };

      // z-indexはpersistがtrueのときのみ保存
      if (this.persist) {
        payload.z = this.zIndex;
      }

      // ★ 位置は persistPosition が true のときのみ保存
      if (this.persistPosition) {
        payload.t = this.top;
        payload.l = this.left;
        payload.r = this.right;
      }

      // ★ サイズは persistSize かつ resizableEffective のときのみ保存
      if (this.persistSize && this.resizableEffective) {
        payload.w  = this.width;
        payload.h  = this.height;
        payload.ah = this.autoHeight;
      }

      try { localStorage.setItem(key, JSON.stringify(payload)); } catch (_) {}
    },
    restoreFromStorage({ restoreZ = true } = {}) {
      // persist または persistPosition のいずれかがtrueなら復元処理を行う
      if (!this.persist && !this.persistPosition) return;
      try {
        const key = this.storageKey || `fw:${this.windowId}`;
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const p = JSON.parse(raw);
        if (p && typeof p === 'object') {
          // ★ 位置は persistPosition が true のときのみ復元
          if (this.persistPosition) {
            let restored = false;
            if (Number.isFinite(p.t)) { this.top = p.t; restored = true; }

            // anchorに応じて適切な座標だけを復元
            if (p.a === 'right' && Number.isFinite(p.r)) {
              this.right = p.r;
              restored = true;
            } else if (Number.isFinite(p.l)) {
              this.left = p.l;
              restored = true;
            }

            // 位置が復元されたらフラグを立てる（オフスクリーンチェックをスキップするため）
            if (restored) this.positionRestoredFromStorage = true;
          }

          // ★ サイズは persistSize かつ resizableEffective のときだけ復元
          if (this.persistSize && this.resizableEffective) {
            if (Number.isFinite(p.w)) this.width = p.w;
            if (p.ah !== undefined) this.autoHeight = !!p.ah;
            if (!this.autoHeight && Number.isFinite(p.h)) this.height = p.h;
          }

          // z-indexはpersistがtrueのときのみ復元
          if (this.persist && restoreZ && Number.isFinite(p.z)) this.zIndex = p.z;
        }
      } catch (_) {}
    },

    /* -------------------- スナップ -------------------- */
    snapToEdges(containerW = window.innerWidth, containerH = window.innerHeight) {
      if (this.snap === 'off') return;
      const s = Math.max(0, this.snapPx | 0);
      const ed = new Set(
          this.snap === 'xy' ? ['top','bottom','left','right'] :
              this.snap === 'x'  ? ['left','right'] :
                  this.snap === 'y'  ? ['top','bottom'] :
                      Array.isArray(this.snapEdges) ? this.snapEdges : []
      );
      // 上下
      if (ed.has('top') && this.top <= s) this.top = 0;
      const currH = this.currentHeight();
      const bottomGap = containerH - (this.top + currH);
      if (ed.has('bottom') && bottomGap <= s) this.top = Math.max(0, containerH - currH);
      // 左右（アンカーに応じて）
      if (this.anchor === 'right') {
        if (ed.has('right') && this.right <= s) this.right = 0;
        const leftGap = containerW - (this.right + this.width);
        if (ed.has('left') && leftGap <= s) this.right = Math.max(0, containerW - this.width);
      } else {
        if (ed.has('left') && this.left <= s) this.left = 0;
        const rightGap = containerW - (this.left + this.width);
        if (ed.has('right') && rightGap <= s) this.left = Math.max(0, containerW - this.width);
      }
    },

    /* -------------------- オフスクリーン対策 -------------------- */
    ensureInitialIfOffscreen() {
      // autoWidthの場合はオフスクリーンチェックをスキップ
      if (this.autoWidth) return;
      // 位置がストレージから復元された場合はオフスクリーンチェックをスキップ
      if (this.positionRestoredFromStorage) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = Math.min(this.width, vw);
      const h = Math.min(this.currentHeight(), vh);
      const x = this.anchor === 'right' ? vw - (this.right + w) : this.left;
      const y = this.top;
      const interW = Math.min(x + w, vw) - Math.max(x, 0);
      const interH = Math.min(y + h, vh) - Math.max(y, 0);
      const visibleEnough = interW > 24 && interH > 24;
      if (!visibleEnough) {
        this.top = this.defaultTop;
        if (typeof this.defaultWidth === 'number') {
          this.width = Math.min(this.defaultWidth, vw);
          this.autoWidth = false;
        } else {
          this.autoWidth = true;
        }
        if (typeof this.defaultHeight === 'number') {
          this.height = Math.min(this.defaultHeight, vh);
          this.autoHeight = false;
        } else {
          this.autoHeight = true;
        }
        if (this.anchor === 'right') this.right = this.defaultRight; else this.left = this.defaultLeft;
        this.top = Math.min(this.top, Math.max(0, vh - this.currentHeight()));
        if (!this.autoWidth) {
          if (this.anchor === 'right') this.right = Math.min(this.right, Math.max(0, vw - this.width));
          else this.left = Math.min(this.left, Math.max(0, vw - this.width));
        }
      }
    },

    /* -------------------- デフォルト位置/サイズへリセット -------------------- */
    resetToDefaultGeometry() {
      this.isMaximized = false;
      this.top = this.defaultTop;
      if (this.anchor === 'right') {
        this.right = this.defaultRight;
        this.left = 0;
      } else {
        this.left = this.defaultLeft;
        this.right = 0;
      }
      if (this.defaultWidth === 'auto') {
        this.autoWidth = true;
      } else if (typeof this.defaultWidth === 'number') {
        this.autoWidth = false;
        this.width = this.defaultWidth;
      }
      if (this.defaultHeight === 'auto' || this.defaultHeight === '' || this.defaultHeight === null || this.defaultHeight === undefined) {
        this.autoHeight = true;
      } else if (typeof this.defaultHeight === 'number') {
        this.autoHeight = false;
        this.height = this.defaultHeight;
      }
    },

    /* -------------------- 閉じる -------------------- */
    close(e) {
      this.$emit('close', {
        id: this.windowId,
        isMaximized: this.isMaximized,
        rect: { top: this.top, left: this.left, right: this.right, width: this.width, height: this.height },
        nativeEvent: e || null,
        timestamp: Date.now(),
      });
      // preventAutoCloseがtrueの場合は自動で閉じない（確認ダイアログ用）
      if (!this.preventAutoClose) {
        this.$store.commit('setFloatingVisible', { id: this.windowId, visible: false });
      }
    },

    /* -------------------- カスタムアイコンクリック -------------------- */
    handleCustomIconClick(iconDef) {
      // eventName が指定されていればそれを emit
      if (iconDef.eventName) {
        this.$emit(iconDef.eventName, iconDef);
      }
      // handler 関数が直接指定されていれば実行
      if (typeof iconDef.handler === 'function') {
        iconDef.handler(iconDef);
      }
    },

    /* -------------------- ドラッグ -------------------- */
    onDivPointerDown(e) {
      if (this.isModal) return; // モーダル時はドラッグ禁止
      // リサイズハンドル上の pointerdown はドラッグ抑止
      if (e.target && e.target.closest && e.target.closest('.resizer')) return;

      // z-index更新（1回だけgetNextZIndexを呼ぶ）
      const newZ = getNextZIndex();
      this.zIndex = newZ;
      this.$nextTick(() => {
        setTimeout(() => {
          // overlayには親FWのz-index + 1を設定（カウンターを消費しない）
          document.querySelectorAll('.v-overlay').forEach((elm) => {
            elm.style.zIndex = newZ + 1;
          });
        }, 30);
      });

      if (this.isMaximized) return;
      this.handlePointerDown(e);
    },
    handlePointerDown(e) {
      if (this.isModal) return; // モーダル時はドラッグ禁止
      if (this.resizing) return;
      if (!this.fullDraggable && this.headerShown && this.$refs.header && !this.$refs.header.contains(e.target)) return;
      this.startDrag(e);
    },
    startDrag(e) {
      // センターモーダル時はドラッグ不可
      if (this.isCenterModal) return;
      this.dragging = true;
      this.startX = e.clientX;
      this.startY = e.clientY;
      if (this.anchor === 'right') this.startLeft = window.innerWidth - (this.right + this.width);
      else this.startLeft = this.left;
      this.startTop = this.top;
      if (e.cancelable) e.preventDefault();
      try { e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId); } catch(_) {}
      document.addEventListener('pointermove', this.onDrag, { passive: false });
      document.addEventListener('pointerup', this.stopDrag, { passive: true });
      document.addEventListener('pointercancel', this.stopDrag, { passive: true });
    },
    onDrag(e) {
      if (!this.dragging) return;
      if (e.cancelable) e.preventDefault();
      const newLeft = this.startLeft + (e.clientX - this.startX);
      const newTop = this.startTop + (e.clientY - this.startY);
      if (this.anchor === 'right') this.right = window.innerWidth - Math.max(0, newLeft) - this.width; // newLeft は負値禁止
      else this.left = Math.max(0, newLeft);
      // 下への移動はヘッダーが画面下に達するまでに制限
      const maxTop = window.innerHeight - this.headerHeight;
      this.top = Math.min(Math.max(0, newTop), maxTop);
    },
    stopDrag(e) {
      this.dragging = false;
      try { e && e.target && e.target.releasePointerCapture && e.target.releasePointerCapture(e.pointerId); } catch(_) {}
      document.removeEventListener('pointermove', this.onDrag);
      document.removeEventListener('pointerup', this.stopDrag);
      document.removeEventListener('pointercancel', this.stopDrag);
      // this.snapToEdges();
      this.saveToStorage();
      this.$nextTick(() => this.updateAutoHeightClamp());
      // 位置変更イベントを発火
      this.$emit('position-change', { top: this.top, left: this.left });
    },

    /* -------------------- リサイズ -------------------- */
    startResize(e, dir) {
      if (!this.resizableEffective || this.isMaximized) return;
      this.resizing = true;
      this.resizeDir = dir;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.startLeft = this.left;
      this.startTop = this.top;
      if (this.autoHeight) { const el = this.getWindowElement(); this.height = el ? el.offsetHeight : 0; this.autoHeight = false; }
      this.startWidth = this.width;
      this.startHeight = this.height;
      this.originalAspect = this.startWidth / this.startHeight;
      if (e.cancelable) e.preventDefault();
      try { e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId); } catch(_) {}
      document.addEventListener('pointermove', this.onResize, { passive: false });
      document.addEventListener('pointerup', this.stopResize, { passive: true });
      document.addEventListener('pointercancel', this.stopResize, { passive: true });
    },
    onResize(e) {
      if (!this.resizing) return;
      if (e.cancelable) e.preventDefault();

      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;

      const hasLeft   = this.resizeDir.includes('left');
      const hasRight  = this.resizeDir.includes('right');
      const hasTop    = this.resizeDir.includes('top');
      const hasBottom = this.resizeDir.includes('bottom');

      let newW = this.startWidth;
      let newH = this.startHeight;

      if (this.keepAspectRatio) {
        const aspect = this.originalAspect || (this.startWidth / Math.max(1, this.startHeight));

        if ((hasLeft || hasRight) && !(hasTop || hasBottom)) {
          if (hasRight) newW = this.startWidth + dx;
          if (hasLeft)  newW = this.startWidth - dx;
          newH = newW / aspect;
          const dH = newH - this.startHeight;
          this.top = Math.max(0, this.startTop - dH / 2);
          if (hasLeft) this.left = this.startLeft + (this.startWidth - newW);
        }
        else if ((hasTop || hasBottom) && !(hasLeft || hasRight)) {
          if (hasBottom) newH = this.startHeight + dy;
          if (hasTop)    newH = this.startHeight - dy;
          newW = newH * aspect;
          const dW = newW - this.startWidth;
          this.left = Math.max(0, this.startLeft - dW / 2);
          if (hasTop) this.top = Math.max(0, this.startTop + (this.startHeight - newH));
        }
        else {
          if (hasRight) newW = this.startWidth + dx;
          if (hasLeft)  newW = this.startWidth - dx;
          if (!(hasLeft || hasRight)) {
            if (hasBottom) newH = this.startHeight + dy;
            if (hasTop)    newH = this.startHeight - dy;
            newW = newH * aspect;
          } else {
            newH = newW / aspect;
          }
          if (hasLeft)  this.left = this.startLeft + (this.startWidth - newW);
          if (hasTop)   this.top  = Math.max(0, this.startTop  + (this.startHeight - newH));
        }
      } else {
        if (hasRight) newW = this.startWidth + dx;
        if (hasLeft)  { newW = this.startWidth - dx; this.left = this.startLeft + dx; }
        if (hasBottom) newH = this.startHeight + dy;
        if (hasTop)    { newH = this.startHeight - dy; this.top = Math.max(0, this.startTop + dy); }
      }

      newW = Math.max(20, newW);
      newH = Math.max(20, newH);

      this.width  = newW;
      this.height = newH;
      this.$emit('width-changed', this.width);
      this.$emit('height-changed', this.height);
    },
    stopResize(e) {
      this.resizing = false;
      try { e && e.target && e.target.releasePointerCapture && e.target.releasePointerCapture(e.pointerId); } catch(_) {}
      document.removeEventListener('pointermove', this.onResize);
      document.removeEventListener('pointerup', this.stopResize);
      document.removeEventListener('pointercancel', this.stopResize);
      // this.top = Math.min(this.top, Math.max(0, window.innerHeight - this.currentHeight()));
      // if (this.anchor === 'right') this.right = Math.min(this.right, Math.max(0, window.innerWidth - this.width));
      // else this.left = Math.min(this.left, Math.max(0, window.innerWidth - this.width));
      // this.snapToEdges();
      this.saveToStorage();
      this.$nextTick(() => this.updateAutoHeightClamp());
    },

    /* -------------------- 最大化/復元 -------------------- */
    maximize() {
      if (this.isMaximized || this.isModal) return; // モーダルでは無効
      this.preMaxState = { top: this.top, left: this.left, right: this.right, width: this.width, height: this.height, autoHeight: this.autoHeight };
      this.isMaximized = true;
      this.left = 0; this.right = 0; this.top = 0;
      this.width = window.innerWidth; this.height = window.innerHeight; this.autoHeight = false;
      this.$nextTick(() => { this.$emit('width-changed', this.width); this.$emit('height-changed', this.height); });
      this.$emit('maximize');
      window.addEventListener('resize', this.syncMaxSize, { passive: true });
      this.saveToStorage();
    },
    restore() {
      if (!this.isMaximized) return;
      window.removeEventListener('resize', this.syncMaxSize);
      if (this.preMaxState) {
        this.top = this.preMaxState.top;
        this.left = this.preMaxState.left;
        this.right = this.preMaxState.right;
        this.width = this.preMaxState.width;
        this.height = this.preMaxState.height;
        this.autoHeight = !!this.preMaxState.autoHeight;
      }
      this.isMaximized = false;
      this.$nextTick(() => { this.$emit('width-changed', this.width); this.$emit('height-changed', this.height); });
      this.$emit('restore');
      this.saveToStorage();
      this.$nextTick(() => this.updateAutoHeightClamp());
    },
    syncMaxSize() {
      if (!this.isMaximized) return;
      this.width = window.innerWidth;
      this.height = window.innerHeight;
      this.$nextTick(() => { this.$emit('width-changed', this.width); this.$emit('height-changed', this.height); });
    },

    /* -------------------- キーボード操作 -------------------- */
    onKeydown(e) {
      if (!this.visible || this.isMaximized || this.isModal) return;

      // 入力要素にフォーカスがある場合はキーイベントを無視
      const target = e.target;
      if (target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      )) {
        return;
      }

      const step = e.shiftKey ? 10 : 1; // モーダルではここまで来ない
      if (["ArrowLeft","ArrowRight","ArrowUp","ArrowDown"].includes(e.key)) e.preventDefault();
      if (e.key === 'ArrowUp')   { this.top = Math.max(0, this.top - step); this.saveToStorage(); }
      if (e.key === 'ArrowDown') { this.top = this.top + step;               this.saveToStorage(); }
      if (e.key === 'ArrowLeft') {
        if (e.altKey && this.resizableEffective) { this.width = Math.max(100, this.width - 10); this.$emit('width-changed', this.width); }
        else if (this.anchor === 'right') this.right = this.right + step;
        else this.left = Math.max(0, this.left - step);
        this.saveToStorage();
      }
      if (e.key === 'ArrowRight') {
        if (e.altKey && this.resizableEffective) { this.width = this.width + 10; this.$emit('width-changed', this.width); }
        else if (this.anchor === 'right') this.right = this.right - step;
        else this.left = this.left + step;
        this.saveToStorage();
      }
      this.$nextTick(() => this.updateAutoHeightClamp());
    },

    /* -------------------- 追加: 表示時/初期表示で width-changed を発火 -------------------- */
    emitWidthNow() {
      this.$emit('width-changed', this.width);
    },

    /* -------------------- auto高さの上限制御 -------------------- */
    updateAutoHeightClamp() {
      if (this.isModal) {
        this.isAutoClamped = false;
        return;
      }
      if (this.resizableEffective) {
        this.isAutoClamped = false;
        return;
      }

      const el = this.getWindowElement();
      if (!el) return;

      const maxH = Math.max(
          100,
          window.innerHeight - this.autoHeightCapOffset - Math.max(0, this.top | 0)
      );

      const contentEl = el.querySelector('.content');
      let natural = this.headerShown ? this.headerHeight : 0;
      if (contentEl) natural += contentEl.scrollHeight;

      const needClamp = this.autoHeight && natural > maxH;

      if (needClamp) {
        this.height = maxH;
        this.autoHeight = false;
        this.isAutoClamped = true;
        this.$nextTick(() => this.$emit('height-changed', this.height));
      } else {
        if (!this.autoHeight) {
          if (natural <= maxH) {
            this.autoHeight = true;
            this.isAutoClamped = false;
            this.$nextTick(() => this.$emit('height-changed', this.currentHeight()));
          } else {
            this.isAutoClamped = true;
            this.height = Math.max(100, Math.min(this.height, maxH));
          }
        } else {
          this.isAutoClamped = false;
        }
      }
    },

    setupContentObserver() {
      const el = this.getWindowElement();
      const contentEl = el?.querySelector('.content');
      if (!contentEl) return;
      this.contentMo?.disconnect();
      this.contentMo = new MutationObserver(() => {
        this.$nextTick(() => this.updateAutoHeightClamp());
      });
      this.contentMo.observe(contentEl, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    },
  },

  mounted() {
    // 初期座標（右基準なら right を採用）
    this.top = this.defaultTop;
    if (this.anchor === 'right') this.right = this.defaultRight; else this.left = this.defaultLeft;

    // モード適用（モーダル or 通常）
    if (this.isModal) this.applyModalLayout();
    else { this.restoreFromStorage(); this.ensureInitialIfOffscreen(); }


    // visible状態で生成されたFW（v-forなど）はwatchが発火しないので初期化
    if (this.visible) this.hasBeenVisible = true;
    if (this.visible && this.zIndex === 0) {
      setTimeout(() => {
        this.zIndex = getNextZIndex();
      }, 150);
    }

    // 初回通知
    this.$nextTick(() => { this.emitWidthNow(); });

    // キー操作
    window.addEventListener('keydown', this.onKeydown, { passive: false });

    // 監視設定
    this.$nextTick(() => {
      this.updateAutoHeightClamp();
      this.setupContentObserver();

      // 遅延フェードイン: マウント時に位置確定後に表示
      if (this.delayedFadeIn && this.visible) {
        if (this.isModal) {
          // モーダル時は即座にフェードイン状態を解除
          this.isFadingIn = false;
        } else {
          setTimeout(() => {
            this.isFadingIn = false;
          }, this.fadeInDelay);
        }
      }
    });

    // 画面回転/リサイズに追従（モーダル時は都度フィット）
    const onWinResize = () => {
      if (this.isModal) this.applyModalLayout();
      else this.updateAutoHeightClamp();
    };
    window.addEventListener('resize', onWinResize, { passive: true });
    // クリーンアップ用に保存
    this._onWinResize = onWinResize;
  },

  watch: {
    mapillaryZindex(value) { this.zIndex = value; },

    // モーダルモードが動的に切り替わるケースに対応
    isModal(val) {
      if (val) this.applyModalLayout();
      else this.applyNormalLayout();
    },

    // 4画面モードの切り替えを監視（センターモーダル ⇔ 通常）
    is4ScreenMode(val) {
      // 表示中のウィンドウのみ処理
      if (!this.visible || this.isModal) return;
      // 4画面モードに入った場合、z-indexを更新して最前面に
      if (val) {
        this.zIndex = getNextZIndex();
      }
    },

    visible(val) {
      // スロット遅延レンダリング: 初回表示時にスロットをマウント
      if (val && !this.hasBeenVisible) this.hasBeenVisible = true;

      // z-index更新（1回だけgetNextZIndexを呼ぶ）
      const newZ = getNextZIndex();
      this.zIndex = newZ;

      // 遅延フェードイン: 表示開始時に一時的に非表示
      if (val && this.delayedFadeIn && !this.isModal) {
        this.isFadingIn = true;
      }

      this.$nextTick(() => {
        setTimeout(() => {
          // overlayには親FWのz-index + 1を設定（カウンターを消費しない）
          document.querySelectorAll('.v-overlay, .bottom-right').forEach((elm) => {
            elm.style.zIndex = newZ + 1;
          });
        }, 30);
        if (val) {
          // ボトムシートモードのVuex状態を更新
          if (this.isBottomSheet) {
            this.$store.commit('setBottomSheetActive', true);
          }
          if (this.isBottomSheet) {
            // ボトムシートモード: 下半分固定（windowStyleで制御）
            this.isFadingIn = false;
          } else if (this.isModal) {
            this.applyModalLayout();
            // モーダル時も遅延フェードイン状態を解除（isSmall500時のレイヤーダイアログ等で必要）
            this.isFadingIn = false;
          } else if (this.isCenterModal) {
            // センターモーダル時は位置復元不要（windowStyleで中央配置を強制）
            // z-indexは既に更新済み、サイズのみ必要に応じて設定
            if (this.defaultWidth) {
              this.width = this.defaultWidth;
            }
            // 遅延フェードイン処理
            this.$nextTick(() => {
              this.emitWidthNow();
              this.updateAutoHeightClamp();
              this.setupContentObserver();
              if (this.delayedFadeIn) {
                setTimeout(() => {
                  this.isFadingIn = false;
                }, this.fadeInDelay);
              }
            });
          } else {
            // 通常モード
            const shouldPersist = this.persist && this.persistMode !== 'never';
            const shouldPersistPosition = this.persistPosition;
            if (shouldPersist || shouldPersistPosition) {
              // ★ ウィンドウが再度開かれるときも位置を復元
              this.restoreFromStorage({ restoreZ: false });
            } else {
              // 永続化しない場合は毎回デフォルト位置/サイズに戻す
              this.resetToDefaultGeometry();
            }
            // defaultWidthを常に適用（コード変更後の更新を反映）
            if (this.defaultWidth && (!shouldPersist || !this.resizableEffective)) {
              this.width = this.defaultWidth;
            }
            // 位置がストレージから復元された場合はオフスクリーンチェックをスキップ
            if (!this.positionRestoredFromStorage) {
              this.ensureInitialIfOffscreen();
            }
            this.$nextTick(() => {
              this.emitWidthNow();
              this.updateAutoHeightClamp();
              this.setupContentObserver();

              // 遅延フェードイン: 位置確定後に表示
              if (this.delayedFadeIn && !this.isModal) {
                setTimeout(() => {
                  this.isFadingIn = false;
                }, this.fadeInDelay);
              }
            });
          }
        } else {
          // ウィンドウが閉じられるとき、モーダルでない場合は位置を保存
          if (!this.isModal) {
            this.saveToStorage();
          }
          // ボトムシートモードが終了したらVuex状態を更新
          if (this.isBottomSheet) {
            this.$store.commit('setBottomSheetActive', false);
          }
          this.contentMo?.disconnect();
          // 閉じる時にフェードイン状態をリセット
          this.isFadingIn = false;
        }
      });
    },

    // defaultWidthが変更された場合、widthを更新
    defaultWidth(newWidth, oldWidth) {
      if (this.visible) {
        if (newWidth === 'auto') {
          this.autoWidth = true;
        } else if (typeof newWidth === 'number') {
          this.autoWidth = false;
          this.width = newWidth;
        }
        this.$emit('width-changed', this.autoWidth ? 'auto' : this.width);
      }
    },
  },

  beforeUnmount() {
    document.removeEventListener('pointermove', this.onDrag);
    document.removeEventListener('pointerup', this.stopDrag);
    document.removeEventListener('pointercancel', this.stopDrag);
    document.removeEventListener('pointermove', this.onResize);
    document.removeEventListener('pointerup', this.stopResize);
    document.removeEventListener('pointercancel', this.stopResize);
    window.removeEventListener('resize', this.syncMaxSize);
    window.removeEventListener('keydown', this.onKeydown);
    if (this._onWinResize) window.removeEventListener('resize', this._onWinResize);
    this.contentMo?.disconnect();
  },
};
</script>

<style scoped>
.draggable-div {
  position: fixed; /* absoluteからfixedに変更：親要素の再レンダリングによる位置飛びを防止 */
  background-color: white;
  box-sizing: border-box;
  box-shadow: var(--oh3-shadow-soft);
  border: 1px solid whitesmoke;
  border-radius: var(--oh3-radius-base);
  transition: opacity 1s;
  -webkit-overflow-scrolling: touch;
  overflow: hidden; /* ルートは常に隠す。スクロールは .content で制御 */
  display: flex;
  flex-direction: column;
}

.draggable-div.maximized { border-radius: 0; }

/* 画面全体を基準に配置（親要素に関係なく viewport 基準） */
.draggable-div.fixed-position {
  position: fixed;
}

/* センターモーダル用オーバーレイ背景（4画面モード時） */
.fw-center-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--oh3-color-overlay-50);
  pointer-events: auto;
}

/* センターモーダル時：中央固定表示 */
.draggable-div.center-modal {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  right: auto !important;
  transform: translate(-50%, -50%);
  box-shadow: 0 8px 32px var(--oh3-color-overlay-30);
  border-radius: var(--oh3-radius-lg);
  max-width: 90vw;
  max-height: 90vh;
}

/* ボトムシートモード（スマホ用）：下半分に固定表示 */
.draggable-div.bottom-sheet {
  position: fixed !important;
  top: 50% !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 50% !important;
  border-radius: var(--oh3-radius-2xl) 16px 0 0 !important;
  box-shadow: 0 -4px 20px var(--oh3-color-overlay-20);
  border: none !important;
  transform: none !important;
}

.draggable-div.bottom-sheet .header {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  height: 48px;
  min-height: 48px;
  padding: 8px 12px;
}

.draggable-div.bottom-sheet .header .title {
  font-size: var(--oh3-font-size-lg);
  font-weight: 600;
}

.draggable-div.bottom-sheet .header .close-btn {
  font-size: 36px !important;
}

.draggable-div.bottom-sheet .content {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* モーダル時（画面いっぱい）：シャドー、ボーダー、角丸を削除 */
.draggable-div.modal {
  box-shadow: none !important;
  border: none !important;
  border-radius: 0 !important;
  outline: none !important;
}

.draggable-div.modal .header {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border: none !important;
  border-bottom: none !important;
  /* 小画面時はヘッダーを大きく */
  height: 48px;
  min-height: 48px;
  padding: 8px 12px;
}

/* モーダル時：タイトルを大きく */
.draggable-div.modal .header .title {
  font-size: var(--oh3-font-size-xl);
  font-weight: 600;
}

/* モーダル時：カスタムアイコンを大きく */
.draggable-div.modal .header .custom-icons {
  gap: 16px; /* アイコン間隔を広げて誤タップ防止 */
  margin-right: var(--oh3-spacing-3); /* バツアイコンとの間隔を広げる（16px + 12px = 28px） */
}
.draggable-div.modal .header .custom-icons button {
  font-size: 28px;
  width: 40px;
  height: 36px;
}

/* モーダル時：ウィンドウコントロールボタンを大きく */
.draggable-div.modal .header .window-controls {
  gap: 16px; /* アイコン間隔を広げて誤タップ防止 */
  margin-right: var(--oh3-spacing-3); /* バツアイコンとの間隔を広げる（16px + 12px = 28px） */
}
.draggable-div.modal .header .window-controls button {
  font-size: 28px;
  width: 40px;
  height: 36px;
}

/* モーダル時：閉じるボタンをさらに大きく（スマホ対応） */
.draggable-div.modal .header .close-btn {
  top: 38%!important;
  font-size: 60px!important; /* より大きく、使用頻度が高いため押しやすく */
}

/* モーダル時：コンテンツ領域の枠線も削除 */
.draggable-div.modal .content {
  border: none !important;
  border-top: none !important;
}

.header {
  position: relative;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  cursor: move;
  background-color: var(--main-color);
  color: white;
  height: 32px;
  min-height: 32px;
  flex-shrink: 0;
  box-sizing: border-box;
  line-height: 24px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  overflow: hidden;
  /* ドラッグ中の画面スクロール/ダブルタップズームを抑止 */
  touch-action: none;
}

.header .title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: large;
  font-weight: 500;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: var(--oh3-spacing-2);
}
.header .title :deep(small) { opacity: 0.9; }

/* simple: headerなし、全体ドラッグ、×はホバー時のみ */
.draggable-div.simple .close-btn {
  position: absolute;
  top: -4px;
  right: 8px;
  color: red;
  opacity: 0;
  font-size: var(--oh3-font-size-4xl);
  transition: opacity 0.2s, color 0.2s;
}
.draggable-div.simple:hover .close-btn { opacity: 1; }

/* normal: ヘッダー内に配置、×は白、大きく、中央寄せ、ホバーで透明度変更 */
.draggable-div.normal .header .close-btn {
  position: absolute;
  top: 45%; /* さらに上に配置 */
  right: 8px;
  transform: translateY(-50%);
  opacity: 1;
  color: white;
  font-size: 42px;
  background: transparent;
  border: none;
  line-height: 1;
  cursor: pointer;
  transition: color 0.2s, font-size 0.2s;
}
.draggable-div.normal .header .close-btn:hover { color: var(--oh3-color-overlay-white-70); }

/* カスタムアイコン（子から送られたアイコン） */
.header .custom-icons {
  position: absolute;
  top: calc(50% - 2px);
  /* right は inline style で動的に指定 */
  transform: translateY(-50%);
  display: flex;
  gap: 8px;
  align-items: center;
}
.header .custom-icons .custom-icon-wrapper {
  display: flex;
  align-items: center;
}
.header .custom-icons button {
  color: white !important;
  font-size: var(--oh3-font-size-xl);
  width: 28px;
  height: 24px;
  background: transparent;
  border: none;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s, background-color 0.2s;
}
.header .custom-icons button:hover {
  color: white !important;
  background-color: rgba(255, 255, 255, 0.2);
}

/* カスタムSVGアイコン */
.custom-svg-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}
.custom-svg-icon :deep(svg) {
  width: 100%;
  height: 100%;
}
/* オン状態（プライマリ色＋白線） */
.custom-svg-icon.ai-on :deep(svg path),
.custom-svg-icon.ai-on :deep(svg circle) {
  fill: #1976D2;
  stroke: #ffffff;
}
/* オフ状態（プライマリ色＋灰色線） */
.custom-svg-icon.ai-off :deep(svg path),
.custom-svg-icon.ai-off :deep(svg circle) {
  fill: #1976D2;
  stroke: #9e9e9e;
}

/* Win風ウィンドウ操作 */
.header .window-controls {
  position: absolute;
  top: 50%;
  right: 48px; /* バツボタン(40px) + margin(8px) */
  transform: translateY(-50%);
  display: flex;
  gap: 6px;
}
.header .window-controls button {
  color: white !important;
  font-size: var(--oh3-font-size-xl);
  width: 28px;
  height: 24px;
  background: transparent;
  border: none;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
}
.header .window-controls button:hover { color: blue !important; }

/* コンテキストヘルプ「?」（close-btnと同じスタイル） */
.header .context-help-btn {
  position: absolute;
  top: calc(45% + 2px);
  transform: translateY(-50%);
  margin-right: 4px;
  background: transparent;
  border: none;
  color: white;
  font-size: var(--oh3-font-size-2xl);
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
}
.header .context-help-btn:hover { color: var(--oh3-color-overlay-white-70); }
/* モーダル時（スマホ等）は？アイコンを大きく */
.modal .header .context-help-btn {
  font-size: 32px;
}

.content {
  flex: 1;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  /* overflow は computed(contentOverflowStyle) 経由 */
}

.resizer {
  position: absolute;
  background: transparent;
  /* リサイズ中の画面スクロール抑止 */
  touch-action: none;
  user-select: none;
  z-index: 5; /* コンテンツより前面に */
}
/* 角ハンドル（デフォルト） */
.top-left    { top: -5px; left: -5px; width: 10px; height: 10px; cursor: nwse-resize; }
.top-right   { top: -5px; right: -5px; width: 10px; height: 10px; cursor: nesw-resize; }
.bottom-left { bottom: -5px; left: -5px; width: 10px; height: 10px; cursor: nesw-resize; }
.bottom-right{ bottom: -5px; right: -5px; width: 30px; height: 30px; cursor: nwse-resize; }
/* 辺ハンドル（デフォルト） */
.top    { top: -4px; left: 8px; right: 8px; height: 8px; cursor: ns-resize; }
.bottom { bottom: -4px; left: 8px; right: 8px; height: 8px; cursor: ns-resize; }
.left   { left: -4px; top: 8px; bottom: 8px; width: 8px; cursor: ew-resize; }
.right  { right: -4px; top: 8px; bottom: 8px; width: 8px; cursor: ew-resize; }

/* ===== タッチ（coarse pointer）向けにヒット領域を拡大 ===== */
@media (pointer: coarse) {
  .top-left    { top: -8px; left: -8px; width: 24px; height: 24px; }
  .top-right   { top: -8px; right: -8px; width: 24px; height: 24px; }
  .bottom-left { bottom: -8px; left: -8px; width: 24px; height: 24px; }
  .bottom-right{ bottom: -8px; right: -8px; width: 36px; height: 36px; }

  .top    { top: -8px; left: 12px; right: 12px; height: 16px; }
  .bottom { bottom: -8px; left: 12px; right: 12px; height: 16px; }
  .left   { left: -8px; top: 12px; bottom: 12px; width: 16px; }
  .right  { right: -8px; top: 12px; bottom: 12px; width: 16px; }

  /* 指で分かるように、わずかにヒット領域を可視化（必要なら消す） */
  .resizer { background: transparent; }
}

/* 印刷時は設定ダイアログと共有ダイアログを非表示 */
@media print {
  .draggable-div[id^="draw-config-window-"],
  .draggable-div[id^="draw-share-window-"] {
    display: none !important;
  }
}
</style>
